import React, { useCallback, useState, useMemo } from "react";
import { makeGet, makePost } from "@culturehq/client";
import { Icon, Panel, PlainButton } from "@culturehq/components";

import { useProfile, useQueryString } from "app/context";
import { useFetch } from "app/hooks";
import { errorService, t, Trans } from "app/utils";

import DotTab from "components/common/DotTab";
import { queryKey as interestQueryKey } from "components/filters/InterestFilter";
import { queryKey as skillQueryKey } from "components/community/filters/SkillFilter";
import { queryKey as programQueryKey } from "components/filters/ProgramFilter";
import ProgressLine from "components/admin/analytics/ProgressLine";
import getColorList from "components/admin/analytics/getColorList";

import AddTagButton from "./AddTagButton";

const TopInterest = ({ interest, onInterestAdded, profileInterests, maxValue, color }) => {
  const { onToggleQueryStringValue } = useQueryString();
  const onClick = () => onToggleQueryStringValue(interestQueryKey, interest.id);

  const addable = profileInterests.every(({ name }) => name !== interest.name);
  const onAdd = useCallback(() => onInterestAdded(interest), [onInterestAdded, interest]);

  return (
    <div className="SidePanel__row">
      <p className="clearfix">
        <PlainButton disabled={interest.id === -1} onClick={onClick}>
          {interest.name} ({interest.userCount})
        </PlainButton>
        {addable && <AddTagButton onClick={onAdd} />}
      </p>
      <ProgressLine
        color={color}
        valueNow={interest.userCount}
        valueMax={maxValue}
      />
    </div>
  );
};

const TopInterests = ({ interests, onInterestAdded, profileInterests }) => {
  const maxValue = Math.max(...interests.map(interest => interest.userCount));
  const colors = useMemo(() => getColorList(interests.length), [interests.length]);
  return (
    interests.length > 0 ? interests.map((interest, index) => (
      <TopInterest
        key={interest.id}
        interest={interest}
        onInterestAdded={onInterestAdded}
        profileInterests={profileInterests}
        maxValue={maxValue}
        color={colors[index]}
      />
    )) : (<span>No users with interests</span>)
  );
};

const TopSkill = ({ skill, onSkillAdded, profileSkills, maxValue, color }) => {
  const { onToggleQueryStringValue } = useQueryString();
  const onClick = () => onToggleQueryStringValue(skillQueryKey, skill.id);

  const addable = profileSkills.every(({ name }) => name !== skill.name);
  const onAdd = useCallback(() => onSkillAdded(skill), [onSkillAdded, skill]);

  return (
    <div className="SidePanel__row">
      <p className="clearfix">
        <PlainButton disabled={skill.id === -1} onClick={onClick}>
          {skill.name} ({skill.userCount})
        </PlainButton>
        {addable && <AddTagButton onClick={onAdd} />}
      </p>
      <ProgressLine
        color={color}
        valueNow={skill.userCount}
        valueMax={maxValue}
      />
    </div>
  );
};

const TopSkills = ({ skills, onSkillAdded, profileSkills }) => {
  const maxValue = Math.max(...skills.map(interest => interest.userCount));
  const colors = useMemo(() => getColorList(skills.length), [skills.length]);
  return (
    skills.length > 0 ? skills.map((skill, index) => (
      <TopSkill
        key={skill.id}
        skill={skill}
        onSkillAdded={onSkillAdded}
        profileSkills={profileSkills}
        maxValue={maxValue}
        color={colors[index]}
      />
    )) : (<span>No users with skills</span>)
  );
};

const TopProgram = ({ program, onProgramAdded, profilePrograms, maxValue, color }) => {
  const { onToggleQueryStringValue } = useQueryString();
  const onClick = () => onToggleQueryStringValue(programQueryKey, program.id);

  const addable = profilePrograms.every(({ name }) => name !== program.name);
  const onAdd = useCallback(() => onProgramAdded(program), [onProgramAdded, program]);

  return (
    <div className="SidePanel__row">
      <p className="clearfix">
        <PlainButton disabled={program.id === -1} onClick={onClick}>
          {program.name} ({program.userCount})
        </PlainButton>
        {addable && <AddTagButton onClick={onAdd} />}
      </p>
      <ProgressLine
        color={color}
        valueNow={program.userCount}
        valueMax={maxValue}
      />
    </div>
  );
};

const TopPrograms = ({ programs, onProgramAdded, profilePrograms }) => {
  const maxValue = Math.max(...programs.map(program => program.userCount));
  const colors = useMemo(() => getColorList(programs.length), [programs.length]);
  return (
    programs.length > 0 ? programs.map((program, index) => (
      <TopProgram
        key={program.id}
        program={program}
        onProgramAdded={onProgramAdded}
        profilePrograms={profilePrograms}
        maxValue={maxValue}
        color={colors[index]}
      />
    )) : (<span>No users with programs</span>)
  );
};

const useInterestAddedCallback = onUpdate => useCallback(
  async interestAdded => {
    try {
      await makePost("/profile/user_interests", { name: interestAdded.name });

      onUpdate(state => ({
        ...state,
        interests: state.interests.map(interest => (
          interest.id === interestAdded.id
            ? ({ ...interest, userCount: interest.userCount + 1 })
            : interest
        )),
        profileInterests: [...state.profileInterests, interestAdded]
      }));
    } catch (error) {
      errorService.error(error);
    }
  },
  [onUpdate]
);

const useSkillAddedCallback = onUpdate => useCallback(
  async skillAdded => {
    try {
      await makePost("/profile/user_skills", { name: skillAdded.name });

      onUpdate(state => ({
        ...state,
        skills: state.skills.map(skill => (
          skill.id === skillAdded.id
            ? ({ ...skill, userCount: skill.userCount + 1 })
            : skill
        )),
        profileSkills: [...state.profileSkills, skillAdded]
      }));
    } catch (error) {
      errorService.error(error);
    }
  },
  [onUpdate]
);

const useProgramAddedCallback = onUpdate => useCallback(
  async programAdded => {
    try {
      await makePost("/profile/user_programs", { name: programAdded.name });

      onUpdate(state => ({
        ...state,
        programs: state.programs.map(program => (
          program.id === programAdded.id
            ? ({ ...program, userCount: program.userCount + 1 })
            : program
        )),
        profilePrograms: [...state.profilePrograms, programAdded]
      }));
    } catch (error) {
      errorService.error(error);
    }
  },
  [onUpdate]
);

const TopTags = ({ options }) => {
  const { profile } = useProfile();
  const [currentTab, setCurrentTab] = useState("interests");

  const descSort = array => (
    array.sort((a, b) => (
      b.userCount - a.userCount
    ))
  );

  const { fetching, json, onUpdate } = useFetch(
    () => Promise.all([
      makeGet("/interests", {
        by: "most",
        department_ids: options.departmentIds,
        location_ids: options.locationIds,
        skill_ids: options.skillIds,
        interest_ids: options.interestIds,
        community: options.community,
        class_year_ids: options.classYearIds,
        club_ids: options.clubIds,
        program_ids: options.programIds,
        degree_ids: options.degreeIds
      }),
      makeGet("/skills", {
        by: "most",
        department_ids: options.departmentIds,
        location_ids: options.locationIds,
        skill_ids: options.skillIds,
        interest_ids: options.interestIds,
        community: options.community
      }),
      makeGet("/programs", {
        by: "most",
        department_ids: options.departmentIds,
        location_ids: options.locationIds,
        skill_ids: options.skillIds,
        interest_ids: options.interestIds,
        community: options.community,
        class_year_ids: options.classYearIds,
        club_ids: options.clubIds,
        program_ids: options.programIds,
        degree_ids: options.degreeIds
      }),
      makeGet("/profile/user_interests"),
      makeGet("/profile/user_skills"),
      makeGet("/profile/user_programs")
    ]).then((
      [interestsResponse, skillsResponse, programsResponse,
        userInterestsResponse, userSkillsResponse, userProgramsResponse]
    ) => {
      const interests = descSort(interestsResponse.interests);
      const skills = descSort(skillsResponse.skills);
      const programs = descSort(programsResponse.programs);
      return {
        interests: interests.slice(0, 5),
        skills: skills.slice(0, 5),
        programs: programs.slice(0, 5),
        profileInterests: userInterestsResponse.userInterests.map(({ interest }) => interest),
        profileSkills: userSkillsResponse.userSkills.map(({ skill }) => skill),
        profilePrograms: userProgramsResponse.userPrograms.map(({ program }) => program)
      };
    }),
    [options]
  );

  const onInterestAdded = useInterestAddedCallback(onUpdate);
  const onSkillAdded = useSkillAddedCallback(onUpdate);
  const onProgramAdded = useProgramAddedCallback(onUpdate);

  const getSecondaryComponentLabel = () => {
    if (profile.organization.isEducational) {
      return "Top Programs";
    }

    return "Top Skills";
  };

  const getSecondaryComponent = () => {
    if (profile.organization.isEducational) {
      return (
        <TopPrograms
          programs={json.programs || []}
          onProgramAdded={onProgramAdded}
          profilePrograms={json.profilePrograms || []}
        />
      );
    }

    return (
      <TopSkills
        skills={json.skills || []}
        onSkillAdded={onSkillAdded}
        profileSkills={json.profileSkills || []}
      />
    );
  };

  return (
    <Panel className="SidePanel">
      <Panel.Heading>
        <Icon icon="arrow-graph-up-right" />
        {" "}
        {
          currentTab === "interests"
            ? <Trans>Top Interests</Trans>
            : <Trans>{getSecondaryComponentLabel()}</Trans>
        }
      </Panel.Heading>
      <Panel.LoaderBody loading={fetching}>
        {
          currentTab === "interests"
            ? (
              <TopInterests
                interests={json.interests || []}
                onInterestAdded={onInterestAdded}
                profileInterests={json.profileInterests || []}
              />
            )
            : getSecondaryComponent()
        }
      </Panel.LoaderBody>
      <Panel.Footer>
        <div role="tablist" aria-label={t("Top interests or skills options")} className="text-center">
          <DotTab
            active={currentTab === "interests"}
            ariaLabel={t("Interests")}
            onClick={() => setCurrentTab("interests")}
          />
          <DotTab
            active={currentTab === "skills"}
            ariaLabel={t("Skills")}
            onClick={() => setCurrentTab("skills")}
          />
        </div>
      </Panel.Footer>
    </Panel>
  );
};

export default TopTags;
